// First-party anonymous visitor tracker. Fire-and-forget; never throws, never blocks.
// Never records names, phone numbers, emails, IPs or typed text.
const URL_BASE = import.meta.env.VITE_SUPABASE_URL as string;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const VID = "nt_vid";
const SES = "nt_session";
const IGNORE = "nt_ignore";
const SESSION_MS = 30 * 60 * 1000;

let enabled = false;
let visitorId = "";
let sessionId = "";
let currentPath = "";
let pageStart = 0;
let scrollMarks = new Set<number>();
let formStarted = false;
let formSubmitted = false;
let lastField = "";
let completedFields = new Set<string>();

const safe = <T extends (...a: any[]) => any>(fn: T) => ((...a: any[]) => { try { return fn(...a); } catch { return undefined; } }) as T;

const rid = () => {
  try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
};

const post = (table: string, body: Record<string, unknown>) => {
  try {
    if (!URL_BASE || !KEY) return;
    fetch(`${URL_BASE}/rest/v1/${table}`, {
      method: "POST",
      keepalive: true,
      headers: { apikey: KEY, Authorization: `Bearer ${KEY}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(body),
    }).catch(() => {});
  } catch { /* ignore */ }
};

const touchSession = () => {
  const now = Date.now();
  let s: { id: string; last: number } | null = null;
  try { s = JSON.parse(localStorage.getItem(SES) || "null"); } catch { /* ignore */ }
  let isNew = false;
  if (!s || !s.id || now - s.last > SESSION_MS) { s = { id: rid(), last: now }; isNew = true; }
  s.last = now;
  try { localStorage.setItem(SES, JSON.stringify(s)); } catch { /* ignore */ }
  sessionId = s.id;
  return isNew;
};

const deviceType = () => {
  const w = window.innerWidth;
  if (/Mobi|Android|iPhone/i.test(navigator.userAgent) || w < 768) return "mobile";
  return w < 1024 ? "tablet" : "desktop";
};

const recordVisit = () => {
  const p = new URLSearchParams(location.search);
  const clip = (v: string | null, n = 200) => (v ? v.slice(0, n) : null);
  let tz: string | null = null;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* ignore */ }
  post("visits", {
    visitor_id: visitorId,
    session_id: sessionId,
    landing_path: location.pathname.slice(0, 300),
    referrer: clip(document.referrer ? document.referrer.split("?")[0] : null, 300),
    utm_source: clip(p.get("utm_source")),
    utm_medium: clip(p.get("utm_medium")),
    utm_campaign: clip(p.get("utm_campaign")),
    utm_term: clip(p.get("utm_term")),
    utm_content: clip(p.get("utm_content")),
    has_gclid: p.has("gclid"),
    ui_language: clip(localStorage.getItem("app-language") || document.documentElement.lang || null, 10),
    device_type: deviceType(),
    screen_width: window.screen?.width || window.innerWidth || null,
    browser_language: clip(navigator.language || null, 20),
    timezone: clip(tz, 64),
    is_bot: /bot|crawler|spider|headless/i.test(navigator.userAgent),
  });
};

export const track = safe((event_name: string, props: Record<string, unknown> = {}) => {
  if (!enabled) return;
  if (touchSession()) recordVisit();
  post("events", { visitor_id: visitorId, session_id: sessionId, event_name, path: location.pathname.slice(0, 300), props });
});

/** IDs to attach to an inquiry so it can be joined to its visit history. */
export const getVisitorIds = () => (enabled ? { visitorId, sessionId } : {});

const leavePage = () => {
  if (!currentPath) return;
  track("time_on_page", { seconds: Math.round((Date.now() - pageStart) / 1000), page: currentPath });
  if (formStarted && !formSubmitted) track("form_abandon", { last_field: lastField || null });
  formStarted = false;
};

const onRoute = () => {
  if (location.pathname === currentPath) return;
  leavePage();
  currentPath = location.pathname;
  pageStart = Date.now();
  scrollMarks = new Set();
  formSubmitted = false;
  lastField = "";
  completedFields = new Set();
  track("page_view", { title: document.title.slice(0, 150) });
  setTimeout(checkFormView, 800);
};

const checkFormView = safe(() => {
  const f = document.querySelector("form");
  if (f && f.querySelector("input, select, textarea")) track("quote_form_view", { page: currentPath });
});

const fieldName = (el: Element) =>
  ((el as HTMLInputElement).name || el.id || (el as HTMLInputElement).type || el.tagName.toLowerCase()).slice(0, 60);

const labelOf = (el: HTMLElement) =>
  (el.getAttribute("data-track") || el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);

const locationOf = (el: HTMLElement) => {
  const s = el.closest("section[id], nav, footer, header, form, section, main");
  if (!s) return "page";
  return (s.id || s.tagName.toLowerCase()).slice(0, 40);
};

export const initVisitorTracker = safe(() => {
  const params = new URLSearchParams(location.search);
  if (params.get("notrack") === "1") localStorage.setItem(IGNORE, "1");
  if (localStorage.getItem(IGNORE) === "1") return;
  visitorId = localStorage.getItem(VID) || "";
  if (!visitorId) { visitorId = rid(); localStorage.setItem(VID, visitorId); }
  enabled = true;
  if (touchSession()) recordVisit();

  // Route changes (SPA)
  const wrap = (k: "pushState" | "replaceState") => {
    const orig = history[k];
    history[k] = function (this: History, ...a: any[]) {
      const r = orig.apply(this, a as any);
      setTimeout(safe(onRoute), 0);
      return r;
    } as any;
  };
  wrap("pushState"); wrap("replaceState");
  window.addEventListener("popstate", safe(onRoute));
  onRoute();

  window.addEventListener("scroll", safe(() => {
    const h = document.documentElement.scrollHeight - window.innerHeight;
    const pct = h <= 0 ? 100 : (window.scrollY / h) * 100;
    for (const m of [25, 50, 75, 100]) {
      if (pct >= m - 0.5 && !scrollMarks.has(m)) { scrollMarks.add(m); track("scroll_depth", { percent: m }); }
    }
  }), { passive: true });

  document.addEventListener("click", safe((e: Event) => {
    const el = (e.target as HTMLElement | null)?.closest?.("a, button") as HTMLElement | null;
    if (!el) return;
    const href = el.getAttribute("href") || "";
    const loc = locationOf(el);
    if (/wa\.me|whatsapp\.com/i.test(href)) return track("whatsapp_click", { location: loc });
    if (/^tel:/i.test(href)) return track("phone_click", { location: loc });
    if (el.closest("[aria-label='Language']")) {
      return track("language_switch", { to: (el.getAttribute("aria-label") || "").replace("Switch to ", "").slice(0, 20) });
    }
    if (el.closest("form") && el.getAttribute("type") !== "submit") return;
    track("cta_click", { label: labelOf(el), location: loc, href: href.split("?")[0].slice(0, 200) || null });
  }), true);

  document.addEventListener("focusin", safe((e: Event) => {
    const el = e.target as HTMLElement;
    if (!el.closest?.("form") || !el.matches("input, select, textarea")) return;
    lastField = fieldName(el);
    if (!formStarted) { formStarted = true; track("quote_form_start", { field: lastField }); }
  }), true);

  document.addEventListener("change", safe((e: Event) => {
    const el = e.target as HTMLInputElement;
    if (!el.closest?.("form") || !el.matches("input, select, textarea")) return;
    const name = fieldName(el);
    lastField = name;
    const filled = el.type === "checkbox" || el.type === "radio" ? true : !!el.value;
    if (filled && !completedFields.has(name)) { completedFields.add(name); track("form_field_complete", { field: name }); }
  }), true);

  document.addEventListener("invalid", safe((e: Event) => {
    const el = e.target as HTMLElement;
    if (el.closest?.("form")) track("quote_form_error", { field: fieldName(el) });
  }), true);

  document.addEventListener("submit", safe(() => { formSubmitted = true; track("quote_form_submit", {}); }), true);

  window.addEventListener("pagehide", safe(leavePage));
  document.addEventListener("visibilitychange", safe(() => {
    if (document.visibilityState === "hidden") leavePage();
    else { pageStart = Date.now(); }
  }));
});

export const trackFormSuccess = () => track("quote_form_success", {});
export const trackFormError = (field: string) => track("quote_form_error", { field: field.slice(0, 60) });
